import { E as EmblaCarousel, C as ClassNames, A as Autoplay, F as Fade } from "./embla-CBF9-Aj4.js";
import { o as onFirstInteractionIdle, k as CustomEvents, e as extractSlideColorSchemes, f as duplicateSlidesForLoop, g as addLayoutShiftPrevention, a as addPrevNextBtnsClickHandlersSSR, b as addDotsBtnsAndClickHandlersSSR, X as addBarsBtnsAndClickHandlersSSR, h as addColorSchemeSyncHandlers } from "./utilities-Da0sjanJ.js";
class SlideshowV2 extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.originalSlideCount = 0;
    this.slideColorSchemes = [];
    this.cleanupFunctions = [];
    this.nodes = {
      embla: null,
      container: null,
      viewport: null,
      prevBtn: null,
      nextBtn: null,
      dots: null,
      bars: null,
      paginationContainer: null
    };
    this.config = {
      layout: false,
      showNavigation: false,
      showDots: false,
      showBars: false,
      autoPlay: false,
      options: {}
    };
  }
  connectedCallback() {
    this.cacheNodes();
    this.parseConfig();
    this.upgradeAboveFoldMedia();
    if (Shopify.designMode) {
      this.init();
    } else {
      onFirstInteractionIdle(() => {
        if (this.isConnected) this.init();
      });
    }
    this._abortController = new AbortController();
    document.addEventListener(CustomEvents.mediaStartedPlaying, (e) => {
      if (this.contains(e.detail.resource) && !e.detail.resource.isBackgroundVideo) {
        this.stopAutoPlay();
      }
    }, { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    var _a;
    (_a = this._abortController) == null ? void 0 : _a.abort();
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) {
      throw new Error("SlideshowV2: .embla element not found");
    }
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    if (!this.nodes.container) {
      throw new Error("SlideshowV2: .embla__container element not found");
    }
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    if (!this.nodes.viewport) {
      throw new Error("SlideshowV2: .embla__viewport element not found");
    }
    this.nodes.slides = Array.from(this.nodes.container.querySelectorAll(".slide-item"));
    const embla = this.nodes.embla;
    this.nodes.prevBtn = embla.querySelector(".embla__snip-button--prev");
    this.nodes.nextBtn = embla.querySelector(".embla__snip-button--next");
    this.nodes.dots = embla.querySelector(".embla__snip-dots");
    this.nodes.bars = embla.querySelector(".embla__snip-bars");
    this.nodes.paginationContainer = embla.querySelector(".slideshow-content-pagination");
  }
  parseConfig() {
    const { dataset } = this;
    this.config.showNavigation = dataset.showNavigation === "true";
    this.config.showDots = dataset.showSlideshowDots === "true";
    this.config.showBars = dataset.showBars === "true";
    this.config.options = this.parseJSON(dataset.options, {});
    this.config.layout = this.parseJSON(dataset.animLayout, false);
    this.config.autoPlay = this.parseJSON(dataset.autoPlay, false);
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      return parsed;
    } catch (error) {
      return fallback;
    }
  }
  // ========== Initialization ==========
  init() {
    if (this.emblaApi) {
      this.destroy();
    }
    const slideCount = this.nodes.container.children.length;
    if (slideCount <= 1) {
      this.handleSingleSlide(slideCount);
      return;
    }
    try {
      const plugins = this.initPlugins();
      this.emblaApi = EmblaCarousel(this.nodes.viewport, this.config.options, plugins);
      this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
      this.originalSlideCount = this.nodes.container.children.length;
      if (this.config.options.loop) {
        this.originalSlideCount = duplicateSlidesForLoop(this.emblaApi, this.nodes.container);
      }
      this.setupFeatures();
    } catch (error) {
      throw error;
    }
  }
  handleSingleSlide(slideCount) {
    this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
    this.originalSlideCount = slideCount;
    if (this.nodes.paginationContainer && this.slideColorSchemes[0]) {
      this.nodes.paginationContainer.classList.add(this.slideColorSchemes[0]);
    }
    this.nodes.embla.classList.add("is-initialized");
  }
  initPlugins() {
    const plugins = [];
    plugins.push(ClassNames({
      snapped: "is-snapped",
      inView: ""
    }));
    if (this.config.autoPlay) {
      plugins.push(Autoplay(this.config.autoPlay));
    }
    if (this.config.layout == false && !(this.hasAttribute("data-above-fold") && this.dataset.animStandardEffect === "none")) {
      plugins.push(Fade());
    }
    return plugins;
  }
  setupFeatures() {
    const layoutShiftCleanup = addLayoutShiftPrevention(this.emblaApi, this.nodes.embla);
    this.registerCleanup(layoutShiftCleanup);
    if (this.config.showNavigation) {
      this.enableNavigation();
    }
    if (this.config.showDots) {
      this.enableDots();
    }
    if (this.config.showBars) {
      this.enableBars();
    }
    this.enableColorSchemeSync();
    this._manageSlideMedia();
  }
  // ========== Public API (Theme Editor) ==========
  /**
   * Scroll to a slide by its 0-based index among the original (non-duplicated) slides.
   * @param {number} index
   */
  scrollToSlide(index) {
    if (!this.emblaApi) return;
    this.emblaApi.scrollTo(index);
  }
  /** Stop Embla AutoPlay plugin if active. No-op otherwise. */
  stopAutoPlay() {
    var _a, _b, _c;
    (_c = (_b = (_a = this.emblaApi) == null ? void 0 : _a.plugins()) == null ? void 0 : _b.autoplay) == null ? void 0 : _c.stop();
  }
  /** Resume Embla AutoPlay plugin if active. No-op otherwise. */
  resumeAutoPlay() {
    var _a, _b, _c;
    if (!this.config.autoPlay) return;
    (_c = (_b = (_a = this.emblaApi) == null ? void 0 : _a.plugins()) == null ? void 0 : _b.autoplay) == null ? void 0 : _c.play();
  }
  // ========== Performance ==========
  /**
   * When the slideshow is above the fold (section_index=0), upgrade the first
   * slide's media from lazy → eager so it loads before the Fade plugin's
   * rAF loop starts, avoiding decode/buffer contention.
   */
  upgradeAboveFoldMedia() {
    if (!this.hasAttribute("data-above-fold")) return;
    const firstSlide = this.querySelector(".slide-item");
    if (!firstSlide) return;
    const img = firstSlide.querySelector("img");
    if (img) {
      img.loading = "eager";
      img.fetchPriority = "high";
    }
    const video = firstSlide.querySelector("video");
    if (video) {
      video.preload = "auto";
      const source = video.querySelector("source");
      if (source) source.setAttribute("fetchpriority", "high");
    }
  }
  // ========== Features ==========
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableDots() {
    this.enablePagination("dots", addDotsBtnsAndClickHandlersSSR);
  }
  enableBars() {
    this.enablePagination("bars", addBarsBtnsAndClickHandlersSSR);
  }
  enablePagination(nodeKey, handlerFunction) {
    const node = this.nodes[nodeKey];
    if (!node || !this.emblaApi) return;
    const originalCount = this.originalSlideCount > 0 ? this.originalSlideCount : null;
    const cleanup = handlerFunction(this.emblaApi, node, originalCount);
    this.registerCleanup(cleanup);
  }
  // ========== Color Scheme Sync ==========
  enableColorSchemeSync() {
    const cleanup = addColorSchemeSyncHandlers(
      this.emblaApi,
      this.nodes.paginationContainer,
      this.slideColorSchemes,
      this.originalSlideCount
    );
    this.registerCleanup(cleanup);
  }
  // ========== Slide Media Lifecycle ==========
  /**
   * Disable parallax & pause videos on non-active slides.
   * Sets data-defer-parallax to prevent auto-init if media-bg
   * hasn't been upgraded yet (script load order race guard).
   */
  _manageSlideMedia() {
    if (!this.emblaApi || !this.nodes.slides.length) return;
    const slideCount = this.nodes.slides.length;
    this.nodes.slides.forEach((slide, i) => {
      if (i === 0) return;
      this._deactivateSlide(slide);
    });
    let prevIndex = 0;
    const onSelect = () => {
      const raw = this.emblaApi.selectedScrollSnap();
      const newIndex = slideCount > 0 ? raw % slideCount : 0;
      if (newIndex === prevIndex) return;
      this._deactivateSlide(this.nodes.slides[prevIndex]);
      this._activateSlide(this.nodes.slides[newIndex]);
      prevIndex = newIndex;
    };
    this.emblaApi.on("select", onSelect);
    this.registerCleanup(() => {
      var _a, _b;
      (_a = this.emblaApi) == null ? void 0 : _a.off("select", onSelect);
      (_b = this.nodes.slides) == null ? void 0 : _b.forEach((slide) => {
        const mb = slide.querySelector('media-bg-component[data-parallax="true"]');
        if (mb) delete mb.dataset.deferParallax;
      });
    });
  }
  _activateSlide(slide) {
    var _a;
    if (!slide) return;
    const mediaBg = slide.querySelector('media-bg-component[data-parallax="true"]');
    if (mediaBg) {
      const inner = mediaBg.querySelector(".media-bg-block-content");
      if (inner) inner.style.animationPlayState = "";
      delete mediaBg.dataset.deferParallax;
      (_a = mediaBg.enableParallax) == null ? void 0 : _a.call(mediaBg);
    }
    const video = slide.querySelector("video");
    if (video) video.play().catch(() => {
    });
  }
  _deactivateSlide(slide) {
    var _a;
    if (!slide) return;
    const mediaBg = slide.querySelector('media-bg-component[data-parallax="true"]');
    if (mediaBg) {
      const inner = mediaBg.querySelector(".media-bg-block-content");
      if (inner) inner.style.animationPlayState = "paused";
      (_a = mediaBg.disableParallax) == null ? void 0 : _a.call(mediaBg);
      mediaBg.dataset.deferParallax = "true";
    }
    const video = slide.querySelector("video");
    if (video) video.pause();
  }
  // ========== Cleanup ==========
  registerCleanup(cleanup) {
    if (cleanup && typeof cleanup === "function") {
      this.cleanupFunctions.push(cleanup);
    }
  }
  destroy() {
    this.cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch (error) {
      }
      this.emblaApi = null;
    }
  }
}
if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", SlideshowV2);
}
